import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../utils/db';

interface UIState {
  isSidebarOpen: boolean;
  isAddHabitModalOpen: boolean;
  achievementAnimation: { habitId: string; date: string } | null;
  soundEnabled: boolean;
  language: 'ja' | 'en';
  theme: 'light' | 'dark' | 'auto';
  hasPromptedPWA: boolean;
  firstVisitDone: boolean;
}

const initialState: UIState = {
  isSidebarOpen: false,
  isAddHabitModalOpen: false,
  achievementAnimation: null,
  soundEnabled: true,
  language: 'ja',
  theme: 'auto',
  hasPromptedPWA: false,
  firstVisitDone: false,
};

// Async thunks - IndexedDB への保存
export const saveSettings = createAsyncThunk(
  'ui/saveSettings',
  async (settings: Partial<UIState>) => {
    const current = await db.settings.get('current');
    if (current) {
      const updated = {
        ...current,
        ...settings,
        lastOpened: new Date()
      };
      // IndexedDB スキーマに合わせる（もし boolean フィールドがあれば）
      await db.settings.put(updated as any);
    }
    return settings;
  }
);

export const loadSettings = createAsyncThunk(
  'ui/loadSettings',
  async () => {
    const settings = await db.settings.get('current');
    return settings;
  }
);

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    openAddHabitModal: (state) => {
      state.isAddHabitModalOpen = true;
    },
    closeAddHabitModal: (state) => {
      state.isAddHabitModalOpen = false;
    },
    showAchievementAnimation: (state, action: PayloadAction<{ habitId: string; date: string }>) => {
      state.achievementAnimation = action.payload;
    },
    hideAchievementAnimation: (state) => {
      state.achievementAnimation = null;
    },
    setLanguage: (state, action: PayloadAction<'ja' | 'en'>) => {
      state.language = action.payload;
    },
    toggleSound: (state) => {
      state.soundEnabled = !state.soundEnabled;
    },
    markPWAPrompted: (state) => {
      state.hasPromptedPWA = true;
    },
    markFirstVisitDone: (state) => {
      state.firstVisitDone = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadSettings.fulfilled, (state, action) => {
      if (action.payload) {
        state.language = action.payload.language;
        state.soundEnabled = action.payload.soundEnabled;
        state.theme = action.payload.theme;
        state.hasPromptedPWA = action.payload.hasPromptedPWA;
        state.firstVisitDone = action.payload.firstVisitDone;
      }
    });
  }
});

export const {
  toggleSidebar,
  openAddHabitModal,
  closeAddHabitModal,
  showAchievementAnimation,
  hideAchievementAnimation,
  setLanguage,
  toggleSound,
  markPWAPrompted,
  markFirstVisitDone,
} = uiSlice.actions;

export default uiSlice.reducer;
